// MailSetupPanel — the six-state setup gate of architecture/MAIL.md §3.2a,
// rendered from `sdk.execute('mail.setup', 'get')` and from nothing else.
//
// The server decides the state (off | ready | reauth | connect | link |
// ask-admin); this file only paints it. That is the whole point of §3.2a: one
// panel, three mount points (the /mail empty state, the header drawer, the
// /settings Mail card), and no surface that invents its own idea of whether
// mail is set up.
//
// TWO CONTRACT FACTS THIS FILE IS BUILT ON
//   • `providers[].steps` is served ONLY when `viewerIsAdmin`. A member gets an
//     empty list, and that is NORMAL — the member states render their own copy
//     plus the provider LABEL, never an empty "instructions" box.
//   • The steps are SERVER CONTENT (the scope list, the consent URL, the
//     Exchange command). The server sends each one as English plus stable
//     codes and the org-specific values its sentence names (`params`); the
//     card translates by those codes (functions/serverCopy.js) and paints the
//     server's English only for a code this bundle does not know. The copyable
//     `code` line is a technical artifact and stays verbatim.
//
// Design: flat, borderless, tokens only (design-direction.md §8/§15 and the
// brand-language weight law {400,500,700}). The single accent on the panel is
// the primary action's solid ink; every other surface is the house
// `overlayStrong` card. No dividers, no shadow, no second hue.

// The panel's action control — declared ONCE so the five call sites below
// (Write / Reconnect / Link / Notify / Set up mail) cannot drift into five
// geometries (Rule 61 + Rule 68: the scale belongs to the primitive, never to
// the call site). Capsule + controlH is the house CTA scale (DESIGN_LANGUAGE
// §6.2); `primary` carries hover / active / focus-visible already (Rule 65).
export const MailSetupAction = {
  // The admin kit's solid-ink primary (AdminActionBtnPrimary: the brand
  // `primary` theme, controlH, the pill, Z2 at 700) and its ONE disabled law:
  // the real `disabled` attribute, cursor not-allowed and the `field-disabled`
  // pair, as a `:disabled` pseudo on the primitive (Rule 65). The disabled
  // step was opacity .55, which the brand's disabled law forbids.
  extends: 'AdminActionBtnPrimary',
  flexShrink: '0'
}

// One provider — fed `childrenAs: 'state'` from `setup.providers`, so its state
// is `{ kind, integrationKind, label, connectable, adminConnected, catalogued,
// steps }`.
//
// `connectable` is the server's answer to "may THIS viewer start a personal
// OAuth for this provider right now" — policy AND a configured platform client.
// A button that cannot act is DISABLED with the reason said out loud beneath
// it, never hidden: a person who may not connect still has to learn why.
export const MailProviderButton = {
  extends: 'MailSetupAction',
  // Not connectable = disabled, and the primitive's disabled law paints it
  // (the `field-disabled` pair). It switched to an undefined `ghost` theme
  // there.
  disabled: (el, s) => !s.connectable,
  text: (el, s) =>
    el.call('polyglot', 'mail.setup.connect.action').replace('{provider}', s.label || s.kind || ''),
  aria: { disabled: (el, s) => (s.connectable ? null : 'true') },
  onClick: (e, el, s) => {
    e.stopPropagation()
    if (!s.connectable) return
    el.call('startMailConnect', s.kind)
  }
}

// One server-served admin step: a titled card whose `code` line (when the step
// carries one) is selectable text plus a Copy button. The clipboard call lives
// in functions/setup.js (`copyMailSetupCode`) — a component never touches
// `navigator` (Rule 64 / FA503).
//
// The title and the body read the bundle by the step's server codes (`id`,
// `bodyCode`, `params` — CORE-MAIL-SETUP-STEPS-AND-ERRORS-NEED-I18N-CODES-1);
// a Georgian workspace read the server's English here before. A row may carry
// `label` (the provider), which the title then leads with.
export const MailSetupStep = {
  extends: 'CommonCard',
  flow: 'y',
  gap: 'Z',

  StepTitle: {
    tag: 'h4',
    margin: '0',
    fontSize: 'Z2',
    fontWeight: '700',
    color: 'title',
    text: (el, s) => el.call('mailSetupStepTitle', s)
  },
  StepBody: {
    tag: 'p',
    margin: '0',
    fontSize: 'Y2',
    fontWeight: '400',
    lineHeight: '1.5',
    color: 'paragraph',
    text: (el, s) => el.call('mailSetupStepBody', s)
  },
  StepCode: {
    if: (el, s) => !!s.code,
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    padding: 'Z A',
    round: 'radiusControl',
    background: 'hover',

    CodeText: {
      tag: 'code',
      minWidth: '0',
      flex: '1 1 auto',
      fontSize: 'Y2',
      lineHeight: '1.5',
      color: 'title',
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      userSelect: 'text',
      text: (el, s) => s.code || ''
    },
    // The copy action — the admin kit's compact tonal button
    // (AdminActionBtnCompact: the `overlay` wash at rest, the house hover /
    // press washes, the one disabled law, 36 px). It was `theme: 'ghost'`, a
    // theme the brand does not define (the census on dev 8a4c8af39 read it on
    // every step of the /admin/mail provider cards).
    CopyBtn: {
      extends: 'AdminActionBtnCompact',
      flexShrink: '0',
      text: '{{ mail.setup.step.copy | polyglot }}',
      onClick: (e, el, s) => {
        e.stopPropagation()
        el.call('copyMailSetupCode', s.code)
      }
    }
  }
}

export const MailSetupPanel = {
  flow: 'y',
  gap: 'A',
  width: '100%',
  minWidth: '0',
  // The measure the copy was written for. Spacing letters are em-based, so the
  // panel pins its own type scale first — otherwise `maxWidth: 'H'` resolves
  // against whatever font size the mount site happens to carry.
  fontSize: 'Z2',
  maxWidth: 'H',
  color: 'paragraph',

  // A root-state read inside a factory does NOT auto-subscribe the element, so
  // every root key this panel branches on is declared (the same arming the
  // header's Mail button and MailOverlay carry).
  subscribeTo: [
    'mailSetup',
    'mailSetupLoading',
    'mailSetupError',
    'mailSetupNotice',
    'mailSetupBusy'
  ],

  // ONE read for three mount points — `loadMailSetup` holds the in-flight flag
  // and a 15 s freshness window, so mounting the panel twice (drawer open over
  // /mail) still issues one request.
  onCreate: (el) => {
    el.call('loadMailSetup')
  },

  // The whole panel is absent when the gate is `off` (the flag is off for this
  // workspace) — §3.2a: "the entry points do not render (no gate)".
  //
  // Every `*Deps` below is FA515 arming, not decoration: a factory's tracked
  // dependency set is only what it actually READ, so a run that short-circuits
  // on `s.root` never subscribes to the key behind it and the element then
  // never re-renders when the gate answers. The declared selectors are read up
  // front, inside the same effect, whatever the control flow.
  ifDeps: [(el, s) => s.root && s.root.mailSetup],
  if: (el, s) => (s.root && s.root.mailSetup ? s.root.mailSetup.state !== 'off' : true),

  // ── The read itself failed (not a state — a fault) ──
  SetupError: {
    ifDeps: [(el, s) => s.root && s.root.mailSetupError],
    if: (el, s) => !!(s.root && s.root.mailSetupError),
    flow: 'y',
    gap: 'Z',

    ErrorTitle: {
      tag: 'h3',
      margin: '0',
      fontSize: 'Z2',
      fontWeight: '700',
      color: 'title',
      text: '{{ mail.setup.error.title | polyglot }}'
    },
    ErrorBody: {
      tag: 'p',
      margin: '0',
      fontSize: 'Y2',
      fontWeight: '400',
      lineHeight: '1.5',
      color: 'caption',
      textDeps: [(el, s) => s.root && s.root.mailSetupError],
      text: (el, s) => (s.root && s.root.mailSetupError && s.root.mailSetupError.message) || ''
    },
    RetryBtn: {
      extends: 'MailSetupAction',
      alignSelf: 'start',
      text: '{{ mail.setup.error.retry | polyglot }}',
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('loadMailSetup', { force: true })
      }
    }
  },

  // ── First read in flight ──
  SetupLoading: {
    ifDeps: [(el, s) => s.root && s.root.mailSetupLoading, (el, s) => s.root && s.root.mailSetup],
    if: (el, s) => !!(s.root && s.root.mailSetupLoading && !s.root.mailSetup),
    tag: 'p',
    margin: '0',
    fontSize: 'Y2',
    fontWeight: '400',
    color: 'caption',
    text: '{{ mail.setup.loading | polyglot }}'
  },

  // ── The state the server answered ──
  SetupBody: {
    ifDeps: [(el, s) => s.root && s.root.mailSetup, (el, s) => s.root && s.root.mailSetupError],
    if: (el, s) => !!(s.root && s.root.mailSetup && !s.root.mailSetupError),
    flow: 'y',
    gap: 'A',

    SetupHeading: {
      tag: 'h3',
      margin: '0',
      fontSize: 'B',
      fontWeight: '700',
      color: 'title',
      lineHeight: '1.2',
      textDeps: [(el, s) => s.root && s.root.mailSetup],
      text: (el, s) => {
        const keys = {
          ready: 'mail.setup.ready.title',
          reauth: 'mail.setup.reauth.title',
          connect: 'mail.setup.connect.title',
          link: 'mail.setup.link.title',
          'ask-admin': 'mail.setup.askAdmin.title'
        }
        const key = keys[(s.root && s.root.mailSetup && s.root.mailSetup.state) || '']
        return key ? el.call('polyglot', key) : ''
      }
    },

    SetupText: {
      tag: 'p',
      margin: '0',
      fontSize: 'Z2',
      fontWeight: '400',
      lineHeight: '1.5',
      color: 'paragraph',
      textDeps: [(el, s) => s.root && s.root.mailSetup],
      text: (el, s) => {
        const keys = {
          ready: 'mail.setup.ready.body',
          reauth: 'mail.setup.reauth.body',
          connect: 'mail.setup.connect.body',
          link: 'mail.setup.link.body',
          'ask-admin': 'mail.setup.askAdmin.body'
        }
        const key = keys[(s.root && s.root.mailSetup && s.root.mailSetup.state) || '']
        return key ? el.call('polyglot', key) : ''
      }
    },

    // The address the reauth / ready states are about — a fact, not chrome, so
    // it is rendered instead of interpolated into a translated sentence.
    SetupAddress: {
      ifDeps: [(el, s) => s.root && s.root.mailSetup],
      if: (el, s) => {
        const st = s.root && s.root.mailSetup && s.root.mailSetup.state
        return st === 'ready' || st === 'reauth'
      },
      tag: 'p',
      margin: '0',
      fontSize: 'Y2',
      fontWeight: '500',
      color: 'title',
      overflowWrap: 'anywhere',
      textDeps: [(el, s) => s.root && s.root.mailSetup],
      text: (el, s) => {
        // Pick the account the STATE is about, not simply the first one the
        // ACL returned. `accounts` includes `disabled` tombstones, so
        // `accounts[0]` could name a mailbox that was disconnected months ago
        // under the words "Reconnect <address> to send" — the person would go
        // and re-authorise the wrong one. `reauth` means "a send-capable
        // account is reauth_required"; `ready` means "a send-capable account
        // is active". Name exactly that account, and print nothing rather
        // than the wrong thing.
        const setup = (s.root && s.root.mailSetup) || {}
        const wanted = setup.state === 'reauth' ? 'reauth_required' : 'active'
        const sendable = (setup.accounts || []).filter((a) => a && a.canSend)
        const pick = sendable.find((a) => a.status === wanted)
        return (pick && pick.address) || ''
      }
    },

    // ── Actions. One row; each entry gates on the state it belongs to. ──
    SetupActions: {
      flow: 'x',
      gap: 'Z1',
      wrap: 'wrap',
      align: 'center',

      // ready → hand off to THE compose entry point (§3.2a). Until
      // MAIL-UI-COMPOSE-1 lands, `openMailCompose` opens /mail with the draft
      // parked on root.mailComposeDraft.
      ComposeBtn: {
        ifDeps: [(el, s) => s.root && s.root.mailSetup],
        if: (el, s) => (s.root && s.root.mailSetup && s.root.mailSetup.state) === 'ready',
        extends: 'MailSetupAction',
        text: '{{ mail.setup.ready.action | polyglot }}',
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('openMailCompose')
        }
      },

      ReconnectBtn: {
        ifDeps: [(el, s) => s.root && s.root.mailSetup],
        if: (el, s) => (s.root && s.root.mailSetup && s.root.mailSetup.state) === 'reauth',
        extends: 'MailSetupAction',
        text: '{{ mail.setup.reauth.action | polyglot }}',
        onClick: (e, el, s) => {
          e.stopPropagation()
          const accounts = (s.root && s.root.mailSetup && s.root.mailSetup.accounts) || []
          const pick = accounts.find((a) => a && a.status === 'reauth_required') || accounts[0]
          el.call('startMailConnect', (pick && pick.provider) || '')
        }
      },

      LinkBtn: {
        ifDeps: [(el, s) => s.root && s.root.mailSetup],
        if: (el, s) => (s.root && s.root.mailSetup && s.root.mailSetup.state) === 'link',
        extends: 'MailSetupAction',
        disabledDeps: [(el, s) => s.root && s.root.mailSetupBusy],
        disabled: (el, s) => (s.root && s.root.mailSetupBusy) === 'link',
        text: '{{ mail.setup.link.action | polyglot }}',
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('linkMailMailbox')
        }
      },

      // ask-admin, and the link state's fallback when the directory match
      // fails: a member asks; an admin gets the admin button instead (below).
      NotifyBtn: {
        ifDeps: [(el, s) => s.root && s.root.mailSetup],
        if: (el, s) => {
          const setup = s.root && s.root.mailSetup
          if (!setup || setup.viewerIsAdmin) return false
          return setup.state === 'ask-admin' || setup.state === 'link'
        },
        // The admin kit's tonal button: in `link` it is the secondary action
        // beside [Link]. In `ask-admin` it is the only way forward, so it takes
        // the solid primary — through a class block, never a reactive theme or
        // fill, so the tonal wash stays static and the framework forces no
        // `!important`. In `link` it was an undefined `ghost` theme.
        extends: 'AdminActionBtn',
        flexShrink: '0',
        // The primary block holds only while the button can act: busy, it is
        // disabled, and the kit's own `:disabled` law paints it (a `:disabled`
        // nested in the class block does not outrank the block's theme).
        isAskAdminPrimary: (el, s) =>
          (s.root && s.root.mailSetup && s.root.mailSetup.state) === 'ask-admin' &&
          (s.root && s.root.mailSetupBusy) !== 'notifyAdmin',
        '.isAskAdminPrimary': { theme: 'primary', fontWeight: '700' },
        disabledDeps: [(el, s) => s.root && s.root.mailSetupBusy],
        disabled: (el, s) => (s.root && s.root.mailSetupBusy) === 'notifyAdmin',
        text: '{{ mail.setup.askAdmin.action | polyglot }}',
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('notifyMailAdmin')
        }
      },

      // An admin never asks another admin — they go and do it. But ONLY in
      // the two states whose missing piece IS the tenant connection:
      // `ask-admin` (no tenant, and this viewer may not connect a personal
      // mailbox) and `link` (a tenant exists, this viewer's mailbox is not
      // matched to it). In `connect` the person can just sign in, and in
      // `reauth` the repair is their own re-authorisation — offering "Set up
      // mail" there sends an admin to a tenant console to fix a personal
      // account. `state !== 'ready'` said all four.
      AdminBtn: {
        ifDeps: [(el, s) => s.root && s.root.mailSetup],
        if: (el, s) => {
          const setup = s.root && s.root.mailSetup
          if (!setup || !setup.viewerIsAdmin) return false
          return setup.state === 'ask-admin' || setup.state === 'link'
        },
        extends: 'MailSetupAction',
        text: '{{ mail.setup.admin.action | polyglot }}',
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('openMailAdminSetup')
        }
      }
    },

    // ── connect → one button per provider, policy-gated by the server ──
    SetupProviders: {
      ifDeps: [(el, s) => s.root && s.root.mailSetup],
      if: (el, s) => (s.root && s.root.mailSetup && s.root.mailSetup.state) === 'connect',
      flow: 'x',
      gap: 'Z1',
      wrap: 'wrap',
      align: 'center',
      childExtends: 'MailProviderButton',
      childrenAs: 'state',
      childrenDeps: [(el, s) => s.root && s.root.mailSetup],
      children: (el, s) => {
        const providers = (s.root && s.root.mailSetup && s.root.mailSetup.providers) || []
        return providers.map((p) => ({ key: p.kind, ...p }))
      }
    },

    // Why the provider buttons cannot act.
    //
    // ⚠️ THE POLICY CANNOT BE THE REASON HERE, AND THE SERVER PROVES IT.
    // `connect` is reached only when `personalAllowed` is TRUE
    // (MailSetupService.computeSetupState) — a policy of `nobody`, or a
    // viewer outside the allowed domains, answers `ask-admin` instead and
    // this element never renders. `connectable` is
    // `personalCheck(provider).ok && platformClient`, so with the policy half
    // already true the only half left is the platform's own client id. The
    // old three-way branch keyed off `policy.allowPersonal` and could
    // therefore print "this workspace does not allow personal mailboxes"
    // directly under two live [Continue with …] buttons — a sentence the
    // server had just contradicted.
    SetupBlocked: {
      ifDeps: [(el, s) => s.root && s.root.mailSetup],
      if: (el, s) => {
        const setup = s.root && s.root.mailSetup
        if (!setup || setup.state !== 'connect') return false
        return !(setup.providers || []).some((p) => p && p.connectable)
      },
      tag: 'p',
      margin: '0',
      fontSize: 'Y2',
      fontWeight: '400',
      lineHeight: '1.5',
      color: 'caption',
      text: '{{ mail.setup.connect.blockedPlatform | polyglot }}'
    },

    // ── The provider names an admin would have to connect (ask-admin) ──
    SetupProviderNames: {
      ifDeps: [(el, s) => s.root && s.root.mailSetup],
      if: (el, s) => (s.root && s.root.mailSetup && s.root.mailSetup.state) === 'ask-admin',
      tag: 'p',
      margin: '0',
      fontSize: 'Y2',
      fontWeight: '500',
      color: 'caption',
      textDeps: [(el, s) => s.root && s.root.mailSetup],
      text: (el, s) => {
        const providers = (s.root && s.root.mailSetup && s.root.mailSetup.providers) || []
        return providers.map((p) => p.label || p.kind).join(' · ')
      }
    },

    // ── The outcome of the last action ──
    // `key` is a bundle key; `message` is the SERVER's own sentence (the 501
    // contract line, a refusal) and is rendered under it — in the session
    // language when the notice carries a config-error `code` the bundle knows
    // (functions/serverCopy.js), verbatim otherwise.
    SetupNotice: {
      ifDeps: [(el, s) => s.root && s.root.mailSetupNotice],
      if: (el, s) => !!(s.root && s.root.mailSetupNotice),
      flow: 'y',
      gap: 'V',
      padding: 'Z A',
      round: 'radiusControl',
      background: 'hover',

      NoticeText: {
        tag: 'p',
        margin: '0',
        fontSize: 'Y2',
        fontWeight: '500',
        lineHeight: '1.5',
        colorDeps: [(el, s) => s.root && s.root.mailSetupNotice],
        color: (el, s) =>
          (s.root && s.root.mailSetupNotice && s.root.mailSetupNotice.tone) === 'error'
            ? 'dangerInk'
            : 'title',
        textDeps: [(el, s) => s.root && s.root.mailSetupNotice],
        text: (el, s) => {
          const notice = (s.root && s.root.mailSetupNotice) || {}
          const text = notice.key ? el.call('polyglot', notice.key) : ''
          return notice.provider ? text.replace('{provider}', notice.provider) : text
        }
      },
      NoticeDetail: {
        hideDeps: [(el, s) => s.root && s.root.mailSetupNotice],
        hide: (el, s) => !(s.root && s.root.mailSetupNotice && s.root.mailSetupNotice.message),
        tag: 'p',
        margin: '0',
        fontSize: 'Y2',
        fontWeight: '400',
        lineHeight: '1.5',
        color: 'caption',
        textDeps: [(el, s) => s.root && s.root.mailSetupNotice],
        text: (el, s) => el.call('mailErrorText', (s.root && s.root.mailSetupNotice) || {})
      }
    },

    // ── Admin instructions — SERVER CONTENT, admins only ──
    // A member's `steps` is an empty array by contract, so this whole block
    // simply does not render for them. That is the normal case, not an error.
    // The steps are the TENANT setup instructions, so they follow the same
    // two states [Set up mail] does. Without a state guard an admin read
    // "Mail is ready" and then six cards about domain-wide delegation
    // underneath it — the server serves `steps` on every read, so the only
    // thing that ever kept them off a `ready` panel was luck.
    SetupSteps: {
      ifDeps: [(el, s) => s.root && s.root.mailSetup],
      if: (el, s) => {
        const setup = s.root && s.root.mailSetup
        if (!setup || !setup.viewerIsAdmin) return false
        if (setup.state !== 'ask-admin' && setup.state !== 'link') return false
        return (setup.providers || []).some((p) => p && (p.steps || []).length)
      },
      flow: 'y',
      gap: 'Z1',

      StepsTitle: {
        tag: 'h4',
        margin: '0',
        fontSize: 'Z2',
        fontWeight: '700',
        color: 'title',
        text: '{{ mail.setup.admin.steps | polyglot }}'
      },
      StepsList: {
        flow: 'y',
        gap: 'Z1',
        childExtends: 'MailSetupStep',
        childrenAs: 'state',
        childrenDeps: [(el, s) => s.root && s.root.mailSetup],
        children: (el, s) => {
          const providers = (s.root && s.root.mailSetup && s.root.mailSetup.providers) || []
          const rows = []
          for (const provider of providers) {
            const steps = (provider && provider.steps) || []
            steps.forEach((step, i) => {
              rows.push({
                key: `${provider.kind}-${i}`,
                // The provider leads the title; MailSetupStep composes the
                // two when it paints, so a translated title keeps it.
                label: provider.label || provider.kind || '',
                id: step.id || '',
                bodyCode: step.bodyCode || '',
                params: step.params || null,
                title: step.title || '',
                body: step.body || '',
                code: step.code || ''
              })
            })
          }
          return rows
        }
      }
    }
  }
}
